import React from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { useToggle } from 'react-use'

import { compile } from '~/apps/ninesegsAnalyzer/routes/paths'
import {
  IconButton,
  type IconButtonProps,
} from '~/components/elements/IconButton'
import {
  StyledButton,
  type StyledButtonProps,
} from '~/components/elements/StyledButton'
import { SuspenseDialog } from '~/components/elements/SuspenseDialog'
import { useNotify } from '~/hooks/useNotify'

import { useBrandAccountId } from '../hooks/useBrandAccountId'
import { useCheckConflictMutation } from '../hooks/useCheckConflictMutation'
import { useCreateStrategyMemoMutation } from '../hooks/useCreateStrategyMemoMutation'
import { useInvalidateMainQuery } from '../hooks/useMainQuery'
import { useUpdateStrategyMemoMutation } from '../hooks/useUpdateStrategyMemoMutation'
import { type InputValues } from '../utils'

import styles from './StrategyMemoSaveButton.css'

export const StrategyMemoSaveButton = () => {
  const invalidateMainQuery = useInvalidateMainQuery()
  const notify = useNotify()
  const { t } = useTranslation()
  const { getValues, reset } = useFormContext<InputValues>()
  const updateStrategyMemoMutation = useUpdateStrategyMemoMutation({
    onSuccess: () => {
      reset(getValues())
      invalidateMainQuery()
      notify({ content: t('saved_2'), style: 'info' })
    },
    onError: () => {
      notify({ content: t('could_not_save_properly_2'), style: 'error' })
    },
  })

  const [isVisibleConflictDialog, toggleIsVisibleConflictDialog] =
    useToggle(false)
  const checkConflictMutation = useCheckConflictMutation({
    onSuccess: (isConflict) => {
      if (isConflict) {
        toggleIsVisibleConflictDialog()
      } else {
        updateStrategyMemoMutation.mutate()
      }
    },
  })
  const handleClick = React.useCallback<EH<IconButtonProps, 'onClick'>>(() => {
    checkConflictMutation.mutate()
  }, [checkConflictMutation])

  const handleOverwriteButtonClick = React.useCallback<
    EH<StyledButtonProps, 'onClick'>
  >(() => {
    toggleIsVisibleConflictDialog()
    updateStrategyMemoMutation.mutate()
  }, [toggleIsVisibleConflictDialog, updateStrategyMemoMutation])

  const navigate = useNavigate()
  const brandAccountId = useBrandAccountId()
  const createStrategyMemoMutation = useCreateStrategyMemoMutation({
    onSuccess: (strategyMemoId) => {
      reset()
      navigate(compile('strategyMemoEdit', { brandAccountId, strategyMemoId }))
    },
    onError: () => {
      notify({ content: t('could_not_save_properly_3'), style: 'error' })
    },
  })
  const handleSaveAsNewButtonClick = React.useCallback<
    EH<StyledButtonProps, 'onClick'>
  >(() => {
    toggleIsVisibleConflictDialog()
    createStrategyMemoMutation.mutate()
  }, [createStrategyMemoMutation, toggleIsVisibleConflictDialog])

  return (
    <>
      <IconButton
        disabled={
          checkConflictMutation.isPending ||
          updateStrategyMemoMutation.isPending ||
          createStrategyMemoMutation.isPending
        }
        fillMode="outline"
        icon="save"
        onClick={handleClick}
      />

      {isVisibleConflictDialog && (
        <SuspenseDialog
          actions={
            <>
              <StyledButton onClick={handleOverwriteButtonClick}>
                {t('overwrite_and_save_1')}
              </StyledButton>

              <StyledButton onClick={handleSaveAsNewButtonClick}>
                {t('save_as_1')}
              </StyledButton>

              <StyledButton
                buttonStyle="light"
                fillMode="outline"
                onClick={toggleIsVisibleConflictDialog}
              >
                {t('dont_save_1')}
              </StyledButton>
            </>
          }
          className={styles.conflictDialog}
          title={t('confirm_1')}
          onClose={toggleIsVisibleConflictDialog}
        >
          {t('this_may_overwrite_changes_made_by_others_continue_anyway_1')}
        </SuspenseDialog>
      )}
    </>
  )
}
